/**
 * PLAT-9968 — write a new value into a timeline element's leaf property
 * via the editor command system.
 *
 * Supports two shapes:
 *
 *   1. **Composition placement + path**: `targetElementId` matches a
 *      top-level `type: 'composition'` element and `elementPath` walks
 *      into its `elements` tree to the source-element leaf.
 *
 *   2. **Top-level overlay (empty path)**: `targetElementId` is itself
 *      the leaf; `elementPath` is `[]`. The command is dispatched
 *      against the placement directly.
 *
 * Delegates to `modifyCompositionProperty` so writes flow through the
 * same editor → `UpdateElementCommand` → `RendererSyncService` path as
 * the Creator Studio side-panel text edits. This keeps the editor
 * timeline model and the renderer state in lockstep — direct
 * `VideoSourceStore.setSource` writes (the earlier implementation)
 * left the editor unaware and the iframe never repainted.
 *
 * Returns `true` only when at least one `commandUpdateElement` actually
 * landed. Returns `false` on any failure mode: empty `targetElementId`,
 * timeline lookup miss, unresolved element path, or command rejection.
 * Callers use the boolean to decide whether to count the save as
 * committed (analytics + UI). `modifyCompositionProperty` logs its own
 * warnings on each failure path.
 */
import { modifyCompositionProperty } from '@src/utility/compositionSettings';

import type { EditorStoreLike } from '@src/utility/compositionSettings';

interface SetParams {
  editorStore: EditorStoreLike;
  /**
   * Top-level timeline element id — a composition placement OR a
   * standalone overlay element.
   */
  targetElementId: string;
  /**
   * Path segments into `targetElement.elements` to reach the leaf. Empty
   * (`[]`) means the target element itself is the leaf — common for
   * standalone overlays whose `text` property lives directly on the
   * top-level element.
   */
  elementPath: ReadonlyArray<number>;
  property: string;
  newValue: string;
  /**
   * Whether the command should be undoable via the editor's undo stack.
   * Defaults to `true` — quick-edit saves should be reversible like any
   * other text edit in the side panel.
   */
  createUndoEntry?: boolean;
}

export function setCompositionFieldValue(params: SetParams): boolean {
  const {
    editorStore,
    targetElementId,
    elementPath,
    property,
    newValue,
    createUndoEntry = true,
  } = params;

  if (!targetElementId) return false;

  return modifyCompositionProperty(
    targetElementId,
    editorStore,
    [Array.from(elementPath)],
    property,
    newValue,
    createUndoEntry,
  );
}
